import React, { useEffect } from "react";
import Ellipse from "../../Assests/Ellipse.png";
import "../Editing/Editing.css";
import PropTypes from 'prop-types';

const formatTime = (timeInSeconds) => {
  if (isNaN(timeInSeconds)) return "00:00";
  const totalSeconds = Math.floor(Math.max(0, timeInSeconds));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const getSegmentConfiguration = (durationInSeconds) => {
  if (durationInSeconds <= 10) {
    return {
      segmentDuration: 1,
      ticksPerSegment: 3,
      subSegmentDuration: 0.25 // 1sec / 4 = 0.25sec
    };
  }
  if (durationInSeconds <= 20) {
    return {
      segmentDuration: 2,
      ticksPerSegment: 3,
      subSegmentDuration: 0.5 // 2sec / 4 = 0.5sec
    };
  }
  if (durationInSeconds <= 30) {
    return {
      segmentDuration: 3,
      ticksPerSegment: 3,
      subSegmentDuration: 0.75 // 3sec / 4 = 0.75sec
    };
  }
  if (durationInSeconds <= 40) {
    return {
      segmentDuration: 4,
      ticksPerSegment: 3,
      subSegmentDuration: 1 // 4sec / 4 = 1sec
    };
  }
  if (durationInSeconds <= 50) {
    return {
      segmentDuration: 5,
      ticksPerSegment: 3,
      subSegmentDuration: 1.25 // 5sec / 4 = 1.25sec
    };
  }
  if (durationInSeconds <= 60) {
    return {
      segmentDuration: 6,
      ticksPerSegment: 3,
      subSegmentDuration: 1.5 // 6sec / 4 = 1.5sec
    };
  }
  const durationInMinutes = durationInSeconds / 60;

  const segmentRules = [
    { maxDuration: 1, segment: 10 },
    { maxDuration: 2, segment: 15 },
    { maxDuration: 3, segment: 20 },
    { maxDuration: 5, segment: 30 },
    { maxDuration: 9, segment: 60 },
    { maxDuration: 18, segment: 120 },
    { maxDuration: 36, segment: 240 },
    { maxDuration: 72, segment: 480 },
    { maxDuration: 90, segment: 600 },
    { maxDuration: 136, segment: 900 },
    { maxDuration: 180, segment: 1200 },
    { maxDuration: 270, segment: 1800 },
    { maxDuration: Infinity, segment: 3600 }
  ];

  const rule = segmentRules.find(r => durationInMinutes <= r.maxDuration);
  const subSegmentDuration = rule?.segment / 4;

  return {
    segmentDuration: rule?.segment,
    ticksPerSegment: 3,
    subSegmentDuration
  };
};

const TimelineTicks = ({ totalTimelineDuration, onDisplayDurationChange }) => {
  useEffect(() => {
    if (onDisplayDurationChange) {
      onDisplayDurationChange(totalTimelineDuration);
    }
  }, [totalTimelineDuration, onDisplayDurationChange]);

  if (totalTimelineDuration <= 0) {
    return (
      <div className="timeline-ticks-container">
        <span className="varying-time">00:00</span>
      </div>
    );
  }

  const { segmentDuration, ticksPerSegment, subSegmentDuration } = getSegmentConfiguration(totalTimelineDuration);
  const totalFullSegments = Math.floor(totalTimelineDuration / segmentDuration);
  const remainingTime = totalTimelineDuration % segmentDuration;
  const hasPartialSegment = remainingTime > 0;
  const timelineElements = [];

  timelineElements.push(
    <span key="time-0" className="varying-time">
      {formatTime(0)}
    </span>
  );

  for (let i = 0; i < totalFullSegments; i++) {
    const segmentStart = i * segmentDuration;

    for (let j = 1; j <= ticksPerSegment; j++) {
      timelineElements.push(
        <div
          key={`space-${i}-${j}`}
          className="remaining-space"
          style={{ flex: subSegmentDuration }}
        />
      );

      timelineElements.push(
        <div key={`ellipse-${i}-${j}`} className="ellipse-container">
          <img src={Ellipse} alt="tick" className="ellipses" />
        </div>
      );
    }

    const remainingSpace = segmentDuration - (ticksPerSegment * subSegmentDuration);
    timelineElements.push(
      <div
        key={`remaining-${i}`}
        className="remaining-space"
        style={{ flex: remainingSpace }}
      />
    );
    timelineElements.push(
      <span key={`time-${i + 1}`} className="varying-time">
        {formatTime(segmentStart + segmentDuration)}
      </span>
    );
  }

  if (hasPartialSegment) {
    const fullEllipses = Math.floor(remainingTime / subSegmentDuration);

    for (let j = 1; j <= fullEllipses; j++) {
      timelineElements.push(
        <div
          key={`partial-space-${j}`}
          className="remaining-space"
          style={{ flex: subSegmentDuration }}
        />
      );

      timelineElements.push(
        <div key={`partial-ellipse-${j}`} className="ellipse-container">
          <img src={Ellipse} alt="tick" className="ellipses" />
        </div>
      );
    }

    const remainingPartial = remainingTime - (fullEllipses * subSegmentDuration);
    if (remainingPartial > 0) {
      timelineElements.push(
        <div
          key="partial-remaining"
          className="remaining-space"
          style={{ flex: remainingPartial }}
        />
      );
    }
  }

  return (
    <div className="timeline-ticks-container">
      {timelineElements}
    </div>
  );
};

TimelineTicks.propTypes = {
  totalTimelineDuration: PropTypes.string,
  onDisplayDurationChange: PropTypes.func,
}

export default TimelineTicks;